import type { ReactElement } from 'react';
import type { Channel, ConversationItem } from '@chatapp-inbox/shared';
import { formatRemaining, useReplyWindow } from '../conversation';
import { Avatar, displayName, formatListTime } from '../conversations';
import { formatFullDate } from './note-time';
import { WINDOW_CLOSED_FACT, formatWindowExpiry } from './reply-window-text';
import styles from './ContactCard.module.css';

export const CHANNEL_LABELS: Record<Channel, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

export const STATUS_LABELS = { open: 'Aberta', closed: 'Encerrada' } as const;

/** Atualização do "faltam X": a cada minuto (o prazo exato, quando a janela fecha, é de `useReplyWindow`). */
const WINDOW_TICK_MS = 60_000;

/**
 * Situação da janela de resposta ("Aberta até 15:20 (faltam 3 h)" ou "Encerrada — …"). Some quando o
 * número não tem regra de janela (`replyWindow` nulo): não há o que dizer.
 */
function ReplyWindowFact({
  conversation,
}: {
  conversation: ConversationItem;
}): ReactElement | null {
  const state = useReplyWindow(conversation, { tickMs: WINDOW_TICK_MS });
  if (state.kind === 'none') return null;
  return (
    <div className={styles.fact}>
      <dt>Janela de resposta</dt>
      <dd>
        {state.kind === 'open' ? (
          <>
            Aberta até{' '}
            <time
              dateTime={new Date(state.expiresAt).toISOString()}
              title={formatFullDate(new Date(state.expiresAt).toISOString())}
            >
              {formatWindowExpiry(state.expiresAt, state.now)}
            </time>{' '}
            {`(faltam ${formatRemaining(state.remainingMs)})`}
          </>
        ) : (
          WINDOW_CLOSED_FACT
        )}
      </dd>
    </div>
  );
}

/**
 * Quem é o contato: avatar, nome e os dados da conversa (telefone, canal, situação, última
 * mensagem). Nome e telefone vêm do cliente: entram sempre como texto (o React os escapa), o nome
 * com `dir="auto"` (pode ser árabe ou hebraico) e o telefone sempre da esquerda para a direita.
 */
export function ContactCard({
  conversation,
  now,
}: {
  conversation: ConversationItem;
  now: Date;
}): ReactElement {
  const { contact, lastMessageAt } = conversation;
  const name = displayName(contact);
  const phone = contact.phone?.trim();

  return (
    <div className={styles.card}>
      <div className={styles.identity}>
        <Avatar contactId={contact.id} name={name} />
        <p dir="auto" className={styles.name}>
          {name}
        </p>
      </div>
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>Telefone</dt>
          <dd dir="ltr" className={styles.phone}>
            {phone || 'Não informado'}
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>Canal</dt>
          <dd>{CHANNEL_LABELS[contact.channel]}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Situação</dt>
          <dd>{STATUS_LABELS[conversation.status]}</dd>
        </div>
        <ReplyWindowFact conversation={conversation} />
        {lastMessageAt && (
          <div className={styles.fact}>
            <dt>Última mensagem</dt>
            <dd>
              <time dateTime={lastMessageAt} title={formatFullDate(lastMessageAt)}>
                {formatListTime(lastMessageAt, now)}
              </time>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}
